<template>
    <BlockUI :blocked="isUploading">
        <div
            class="documents"
            @dragover.prevent
            @dragenter="(e) => toggleDrag(true, e)"
        >
            <DataTable
                :value="artefacts"
                v-if="artefacts"
                selectionMode="single"
                @row-click="
                    (event: DataTableRowClickEvent) => {
                        // Forward the click to the router link inside.
                        (event.originalEvent.target as HTMLElement)
                            .querySelector('a')
                            ?.click();
                    }
                "
            >
                <template #header>
                    <div class="title-and-controls">
                        <h2>Documents</h2>
                        <FileAdd
                            v-if="isEditing"
                            label="Add document"
                            header="Add document"
                            size="small"
                            :entity="entity"
                            :navigateTo="navigateTo"
                            :showToastOnSave="true"
                            :artefacts="artefacts"
                        />
                    </div>
                </template>

                <template #empty>
                    <div>No documents</div>
                </template>
                <Column field="name" header="Name">
                    <template #body="{ data, field }">
                        <RouterLink
                            class="document-link"
                            :to="{
                                name: navigateTo,
                                params: {
                                    ...route.params,
                                    artefactId: data.identity,
                                },
                            }"
                        >
                            <div class="document-row">
                                <div
                                    class="document-icon"
                                    v-html="getIconForMimetype(data.mimetype)"
                                ></div>
                                {{ data[field] }}
                            </div>
                        </RouterLink>
                    </template>
                </Column>
            </DataTable>
            <div
                v-if="isDragging"
                class="dropzone"
                @dragover.prevent
                @drop.prevent="(e) => uploadFiles(e.dataTransfer?.files)"
                @dragleave="(e) => toggleDrag(false, e)"
            >
                <h2>Drop your files to add</h2>
            </div>
        </div>
    </BlockUI>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import type { ArtefactMeta } from "@/lib/artefacts";
import type { DataTableRowClickEvent } from "primevue/datatable";
import type { Domain } from "@/composables/domain-manager";
import type { Process } from "@/composables/process-manager";
import type { Product } from "@/composables/product-manager";
import type { Task } from "@/stores/task-store";
import type { Role } from "@/composables/role-manager";
import { useRoute } from "vue-router";
import FileAdd from "@/components/FileAdd.vue";
import { getIcon } from "material-file-icons";
import { mime } from "@/utils";
import { useUniqueArtefactManager } from "@/composables/unique-artefact-manager";
import { useArtefactsStore } from "@/lib/artefacts/user";

const route = useRoute();

const navigateTo = "documentPage";
const props = defineProps<{
    isEditing: boolean;
    entityType:
        | "domain"
        | "product"
        | "process"
        | "task"
        | "role"
        | "gateway"
        | "event"
        | "flow";
    entity: Domain | Product | Process | Task | Role;
    artefacts: ArtefactMeta[];
}>();

const { isUploading, checkConflictsAndAddFiles } = useUniqueArtefactManager(
    toRef(() => props.entity.user_artefact_group_id || ""),
    toRef(() => props.artefacts || [])
);
const { updateArtefacts } = useArtefactsStore();

const isDragging = ref(false);

const toggleDrag = (toggle: boolean, event: DragEvent) => {
    event.preventDefault();
    isDragging.value = toggle;
};

const uploadFiles = async (files?: FileList | null) => {
    isDragging.value = false;
    if (!files || !files.length) {
        return;
    }
    await checkConflictsAndAddFiles(files, true, updateArtefacts);
};

const getIconForMimetype = (mimetype: string) => {
    if (mimetype.startsWith("application/vnd.google-apps")) {
        return '<i class="pi pi-google"></i>';
    }
    const filename = `.${mime.getExtension(mimetype)}`;
    return getIcon(filename).svg;
};
</script>

<style scoped lang="scss">
.title-and-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

a.document-link,
a.task-link {
    text-decoration: none;
}

.document-row {
    display: flex;
    gap: 0.2rem;
    align-items: center;
}
.document-icon {
    height: 1.5rem;
    width: 1.5rem;
    text-align: center;

    :deep(> *) {
        margin: auto;
    }

    :deep(> .pi) {
        height: 1.5rem;
        line-height: 1.5rem;
    }
}

.documents {
    position: relative;

    .dropzone {
        z-index: 1;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex: 1;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0.75);
        outline: 2px dashed var(--stekz-primary-color);

        * {
            pointer-events: none;
        }
    }
}
</style>
